import React, { FC } from 'react';
import styled from 'styled-components';
import { AirtableRemoteWorkAuthorsData } from '../../../graphql-types';
import { useEventTracker } from '../../utils/analytics/useEventTracker';
import { gray, primaryColor } from '../../utils/styles';
import { rhythm } from '../../utils/typography';
import { Accordion } from '../Accordion';
import { CardAccordion } from '../CardAccordion';

const ReadMore = styled.a`
  display: flex;
  justify-content: flex-end;
  color: ${primaryColor};
  font-weight: 500;
  &:hover {
    font-weight: 700;
  }
  &:after {
    content: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAQElEQVR42qXKwQkAIAxDUUdxtO6/RBQkQZvSi8I/pL4BoGw/XPkh4XigPmsUgh0626AjRsgxHTkUThsG2T/sIlzdTsp52kSS1wAAAABJRU5ErkJggg==);
    color: ${primaryColor};
    margin: 0 3px 0 5px;
  }
`;

export const PublicationHr = styled.hr`
  margin-bottom: ${rhythm(1)};
`;

const PublicationTakeaway = styled.p`
  white-space: pre-wrap;
`;

const PublicationTitle = styled.h3`
  margin-top: 0;
  transition: color 0.3s;
`;

const PublicationSubtitle = styled.p`
  color: ${gray};
  margin: 0;
`;

const Publication = styled.div`
  width: 100%;
  &:hover ${PublicationTitle} {
    color: ${primaryColor};
  }
`;

const PublicationSource = styled.span`
  display: inline-block;
  &:first-letter {
    text-transform: capitalize !important;
  }
`;

export type PublicationsProps = {
  list: AirtableRemoteWorkAuthorsData['publications'];
};

export const Publications: FC<PublicationsProps> = ({ list }) => {
  const trackClick = useEventTracker('click');

  return (
    <Accordion>
      {list
        ?.sort(
          (a, b) => new Date(b?.data?.publishDate || null).getTime() - new Date(a?.data?.publishDate || null).getTime(),
        )
        .map((publication, index) => {
          const { name, url, description, publishDate, takeaways } = publication?.data || {};
          const publicationSource = url && new URL(url).hostname.replace('www.', '');
          return (
            <CardAccordion key={publication?.id} index={index}>
              <Accordion.Header
                id={publication?.id!}
                index={index}
                onClick={() => trackClick({ label: name || '', category: 'card-click' })}
              >
                <Publication>
                  <PublicationTitle>{name}</PublicationTitle>
                  <PublicationSubtitle>
                    {publishDate && <span>{publishDate}</span>}
                    <>
                      {publishDate && <span> - </span>}
                      {publicationSource && <PublicationSource>{publicationSource}</PublicationSource>}
                    </>
                  </PublicationSubtitle>
                </Publication>
              </Accordion.Header>
              <Accordion.Section id={publication?.id!} index={index}>
                <p>{description}</p>
                {takeaways && (
                  <>
                    <h5>Key Takeaways</h5>
                    <PublicationTakeaway>{takeaways}</PublicationTakeaway>
                  </>
                )}
                {url && (
                  <ReadMore
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackClick({ label: name || '', category: 'outbound' })}
                  >
                    View on {publicationSource}
                  </ReadMore>
                )}
              </Accordion.Section>
            </CardAccordion>
          );
        })}
    </Accordion>
  );
};
